import { mlcServiceLimit } from '@felix/domain';
import { formatDay } from '../../format.js';
import type { RoleList } from '../settings/setup-types.js';
import type {
  AmendRequest,
  CrewMovementRow,
  MovementPort,
  MovementTab,
  RankOption,
  SignOffReason,
  SignOffRequest,
  SignOnRequest,
} from './types.js';

/**
 * Sign On / Off ekranının ve pencerelerinin saf modeli (ADR 0131 §3; `gemi-personel.jpeg` panel 5).
 * Kurallar sunucuda (`@felix/domain` `crew-movement.ts`); burada süzgeç, satır işlemleri ve
 * gönderilecek gövde.
 */

export const MOVEMENT_PAGE_SIZE = 10;

/** Varsayılan tarih aralığı: bu yıl (tasarım 5 "01.01.2025 - 31.12.2025"). */
export function defaultMovementRange(today: string): { from: string; to: string } {
  const year = today.slice(0, 4);
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

/** "All Ranks" seçenekleri: satırlardaki rütbeler, ada göre sıralı. */
export function movementRanks(
  rows: readonly CrewMovementRow[],
  locale: string,
): { value: string; label: string }[] {
  const byId = new Map<string, string>();
  for (const row of rows) byId.set(row.roleId, row.rank ?? '—');
  return [...byId]
    .map(([value, label]) => ({ value, label }))
    .sort((x, y) => x.label.localeCompare(y.label, locale));
}

export function viewMovements(
  rows: readonly CrewMovementRow[],
  filter: { vesselId: string | null; roleId: string | null },
): CrewMovementRow[] {
  return rows.filter(
    (row) =>
      (filter.vesselId === null || row.vesselId === filter.vesselId) &&
      (filter.roleId === null || row.roleId === filter.roleId),
  );
}

export function movementPageOf<T>(rows: readonly T[], page: number): T[] {
  return rows.slice((page - 1) * MOVEMENT_PAGE_SIZE, page * MOVEMENT_PAGE_SIZE);
}

export type MovementAction = 'signOff' | 'rankChange' | 'amend' | 'cancel' | 'revoke' | 'history';

/** Atama henüz katılmamış (katılış geminin bugününden sonra): rütbesi Amend ile değişir. */
export function notJoinedYet(row: { validFrom: string }, today: string): boolean {
  return row.validFrom > today;
}

/**
 * Atamanın düzeltme ve terfi işlemleri: açık atama Sign off ve Rank change alır. Katılmamış
 * kişide Amend öne çıkar ve Rank change kapalı gösterilir (sahip 2026-10-31: katılıştan önce terfi
 * olmaz, rütbe Amend ile düzeltilir; `movementActionHint`).
 */
export function editActions(
  row: { validFrom: string; validTo: string | null },
  today: string,
): MovementAction[] {
  if (row.validTo !== null) return ['amend'];
  return notJoinedYet(row, today)
    ? ['amend', 'signOff', 'rankChange']
    : ['signOff', 'rankChange', 'amend'];
}

/** Menüde kapalı işlemin ipucu (`signOnOff.action.<anahtar>`); `null`: işlem açık. */
export function movementActionHint(
  action: MovementAction,
  row: { validFrom: string },
  today: string,
): string | null {
  return action === 'rankChange' && notJoinedYet(row, today) ? 'rankChangeNotJoined' : null;
}

/**
 * Satır işlemlerinin yetkisi (`GET /api/office/crew/movements`): `canEdit` (`users.edit`),
 * `canVoid` (`users.void`). Ajan kararı (ADR 0151 §4): Rank change yeni atama satırı açar, bu
 * yüzden `canCreate` (`users.create`) de ister; kişinin kendi ataması yalnız yöneticiden (U39,
 * `packages/db/src/crew-movements.ts` `authorizeEdit`). Eski sunucu yeni alanları göndermezse
 * eski davranış sürer; sunucu yine denetler.
 */
export interface MovementRights {
  canEdit: boolean;
  canVoid: boolean;
  canCreate?: boolean;
  actorId?: string;
  actorIsAdmin?: boolean;
  /** Yönetici rolleri (ADR 0150 §1): yönetici rütbeli satır yönetici olmayana salt okunur. */
  adminRoleIds?: readonly string[];
  /**
   * Gemi kapsamlı yönetici şirket rolü taşıyan kişiler (ADR 0150 §1): gemi listeleri yalnız
   * yöneticiden; satırlarında yönetici olmayana işlem yok.
   */
  adminScopeUserIds?: readonly string[];
}

/** Yönetici olmayan işlemi yapanın kendi satırı: işlem yok (U39; ADR 0151 §4). */
function ownRowLocked(
  userId: string,
  actor: Pick<MovementRights, 'actorId' | 'actorIsAdmin'>,
): boolean {
  return actor.actorIsAdmin === false && actor.actorId !== undefined && actor.actorId === userId;
}

/**
 * Yönetici rütbeli satır (ADR 0150 §1): rütbe yönetici rolüyse (ör. tür `any` Company
 * Administrator) Sign off, Rank change, Amend, Cancel joining ve Revoke yalnız yöneticiden; yazma
 * yolu işlemin dokunduğu rütbelere bakar. Eski sunucu `adminRoleIds` göndermezse kısıt yok.
 */
export function adminRankLocked(
  roleId: string,
  actor: Pick<MovementRights, 'actorIsAdmin' | 'adminRoleIds'>,
): boolean {
  return actor.actorIsAdmin === false && (actor.adminRoleIds ?? []).includes(roleId);
}

/**
 * Gemi kapsamlı yönetici şirket rolü taşıyan kişi (ADR 0150 §1 `assertAdminScopeChange`): her
 * Sign On / Off işlemi kişinin gemi listesini değiştirir, yönetici yetkisini yeni gemiye taşır ya
 * da daraltır; yalnız yöneticiden. Eski sunucu `adminScopeUserIds` göndermezse kısıt yok.
 */
export function adminScopeUserLocked(
  userId: string,
  actor: Pick<MovementRights, 'actorIsAdmin' | 'adminScopeUserIds'>,
): boolean {
  return actor.actorIsAdmin === false && (actor.adminScopeUserIds ?? []).includes(userId);
}

/**
 * Satır menüsü (ADR 0131 §7): açık atama Sign off ve Rank change alır; Pending katılış iptal
 * edilir (`users.void`); ayrılmış atamanın ayrılışı geri alınır. `null`: gemi, işlem yok.
 * Yönetici olmayanın kendi satırında, yönetici rütbeli satırda ve gemi kapsamlı yönetici şirket
 * rolü taşıyan kişinin satırında yalnız geçmiş; Rank change `canCreate` ister (ADR 0151 §4).
 */
export function rowActions(
  row: CrewMovementRow,
  tab: MovementTab,
  can: MovementRights | null,
): MovementAction[] {
  if (can === null) return [];
  if (
    !can.canEdit ||
    ownRowLocked(row.userId, can) ||
    adminRankLocked(row.roleId, can) ||
    adminScopeUserLocked(row.userId, can)
  ) {
    return ['history'];
  }
  const actions: MovementAction[] = editActions(row, row.today).filter(
    (action) => action !== 'rankChange' || can.canCreate !== false,
  );
  const open = row.validTo === null;
  if (tab === 'joining' && row.status === 'pending' && can.canVoid) actions.push('cancel');
  if (!open && tab === 'sign_off') actions.push('revoke');
  actions.push('history');
  return actions;
}

/**
 * Add Joining kişi listesi: yönetici olmayan kendini ve gemi kapsamlı yönetici şirket rolü taşıyan
 * kişiyi katılışa yazamaz (U39; ADR 0150 §1; ADR 0151 §4). Eski sunucu alanları göndermezse liste
 * olduğu gibi kalır.
 */
export function signOnPeople<T extends { value: string }>(
  people: readonly T[],
  actor: Pick<MovementRights, 'actorId' | 'actorIsAdmin' | 'adminScopeUserIds'>,
): T[] {
  return people.filter(
    (person) => !ownRowLocked(person.value, actor) && !adminScopeUserLocked(person.value, actor),
  );
}

/**
 * Rütbe seçicilerinin seçenekleri (Sign On, Rank change, Amend, kartla Sign On): rol listesinin
 * etkin `vessel` ve `any` türlü rolleri (ADR 0143 §4; eski sunucu türü göndermez, `any` sayılır).
 * Yönetici olmayana yönetici rolü sunulmaz (ADR 0150 §1; ADR 0151 §4): yazma yolu reddeder.
 */
export function rankChoices(
  list: Pick<RoleList, 'roles' | 'actorIsAdmin' | 'adminRoleIds'>,
): RankOption[] {
  const hidden = new Set(list.actorIsAdmin === false ? (list.adminRoleIds ?? []) : []);
  return list.roles
    .filter(
      (role) =>
        role.deletedAt === null && (role.kind ?? 'any') !== 'office' && !hidden.has(role.id),
    )
    .map((role) => ({ id: role.id, name: role.name }));
}

const trimmed = (value: string) => value.trim();
const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

function portOf(name: string, code: string): MovementPort | undefined {
  const port = { name: orNull(name), code: orNull(code)?.toUpperCase() ?? null };
  return port.name === null && port.code === null ? undefined : port;
}

export interface SignOnForm {
  userId: string;
  vesselId: string;
  roleId: string;
  date: string;
  portName: string;
  portCode: string;
  contractEnd: string;
  contractNo: string;
  contractReason: string;
  /** Kayıtlı aylık maaş (ADR 0165 §1), ana birimde metin; boşsa gönderilmez. */
  monthlyWage: string;
  wageCurrency: string;
}

export function emptySignOnForm(): SignOnForm {
  return {
    userId: '',
    vesselId: '',
    roleId: '',
    date: '',
    portName: '',
    portCode: '',
    contractEnd: '',
    contractNo: '',
    contractReason: '',
    monthlyWage: '',
    wageCurrency: 'USD',
  };
}

/**
 * Katılış alanları hazır mı (kişi hariç): gemi, rütbe, tarih; kontrat MLC son gününü aşıyorsa
 * gerekçe (ADR 0076 §7). Sign On penceresi ve New Crew Card ▸ "Sign on now" ortak kullanır.
 */
export function signOnFieldsReady(form: Omit<SignOnForm, 'userId'>): boolean {
  if (form.vesselId === '' || form.roleId === '' || form.date === '') return false;
  return !mlcLimitHint(form.date, form.contractEnd)?.exceeds || form.contractReason.trim() !== '';
}

export function signOnReady(form: SignOnForm): boolean {
  return form.userId !== '' && signOnFieldsReady(form);
}

export function signOnRequest(form: SignOnForm): SignOnRequest {
  return { userId: form.userId, ...signOnBody(form) };
}

/** Katılışın gövdesi (kişi hariç); New Crew Card ▸ "Sign on now" kartla birlikte gönderir. */
export function signOnBody(form: Omit<SignOnForm, 'userId'>): Omit<SignOnRequest, 'userId'> {
  const port = portOf(form.portName, form.portCode);
  const contractNo = orNull(form.contractNo);
  const contractReason = orNull(form.contractReason);
  const wage = Number(form.monthlyWage);
  return {
    vesselId: form.vesselId,
    roleId: form.roleId,
    date: form.date,
    ...(port ? { port } : {}),
    ...(form.contractEnd ? { contractEnd: form.contractEnd } : {}),
    ...(contractNo ? { contractNo } : {}),
    ...(contractReason ? { contractReason } : {}),
    // Maaş küçük birimde (ADR 0165 §1); boş ya da sıfır gönderilmez
    ...(form.monthlyWage.trim() !== '' && Number.isFinite(wage) && wage > 0
      ? { wage: { monthlyWage: Math.round(wage * 100), currency: form.wageCurrency } }
      : {}),
  };
}

/**
 * MLC'ye göre gemide son gün (ADR 0076 §7 yardım metni): katılış + 12 ay − 1 gün; kontrat bunu
 * aşıyorsa gerekçe istenir. Kesintisiz hizmet zincirini sunucu hesaplar; burada yalnız ipucu.
 */
export function mlcLimitHint(
  joiningDate: string,
  contractEnd: string,
): { limit: string; exceeds: boolean } | null {
  if (joiningDate === '') return null;
  const limit = mlcServiceLimit(joiningDate);
  return { limit, exceeds: contractEnd !== '' && contractEnd > limit };
}

export interface SignOffForm {
  date: string;
  reason: string;
  remarks: string;
  portName: string;
  portCode: string;
}

export function signOffReady(form: SignOffForm): boolean {
  if (form.date === '' || form.reason === '') return false;
  return form.reason !== 'other' || trimmed(form.remarks) !== '';
}

export function signOffRequest(form: SignOffForm, rowVersion: number): SignOffRequest {
  const port = portOf(form.portName, form.portCode);
  const remarks = orNull(form.remarks);
  return {
    rowVersion,
    date: form.date,
    ...(port ? { port } : {}),
    reason: form.reason as SignOffReason,
    ...(remarks ? { remarks } : {}),
  };
}

export interface AmendForm {
  /** Sekmenin tarihi (katılış ya da ayrılış). */
  date: string;
  portName: string;
  portCode: string;
  contractNo: string;
  reason: string;
  /** Rütbe: katılmamış kişinin rütbesi Amend ile değişir (sahip 2026-10-31). */
  roleId: string;
}

export function amendFormOf(row: CrewMovementRow): AmendForm {
  return {
    date: row.date,
    portName: row.port ?? '',
    portCode: row.portCode ?? '',
    contractNo: row.contractNo ?? '',
    reason: '',
    roleId: row.roleId,
  };
}

/** Amend gövdesi: yalnız değişen alanlar; gerekçe doluysa. */
export function amendRequest(
  row: CrewMovementRow,
  tab: MovementTab,
  form: AmendForm,
): AmendRequest {
  const request: AmendRequest = { rowVersion: row.rowVersion };
  const reason = orNull(form.reason);
  if (reason) request.reason = reason;
  if (form.date !== row.date) {
    if (tab === 'joining') request.validFrom = form.date;
    else request.validTo = form.date;
  }
  const portName = orNull(form.portName);
  const portCode = orNull(form.portCode)?.toUpperCase() ?? null;
  if (portName !== row.port || portCode !== row.portCode) {
    const port = { name: portName, code: portCode };
    if (tab === 'joining') request.joinPort = port;
    else request.signOffPort = port;
  }
  const contractNo = orNull(form.contractNo);
  if (contractNo !== row.contractNo) request.contractNo = contractNo;
  if (form.roleId !== '' && form.roleId !== row.roleId) request.roleId = form.roleId;
  return request;
}

/** Sunucu hata kodunun metni (`signOnOff.errors.*`). */
export function movementErrorKey(code: string): string {
  return `signOnOff.errors.${code}`;
}

/**
 * Hata metninin değerleri: sunucu ayrıntısı bir günse `{{date}}` olur (ör.
 * `RANK_CHANGE_BEFORE_JOINING` katılış tarihini taşır).
 */
export function movementErrorOptions(error: unknown): Record<string, string> {
  const detail = (error as { detail?: unknown } | null)?.detail;
  return typeof detail === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(detail)
    ? { date: formatDay(detail) }
    : {};
}
